'use client';

import { useId, useState } from 'react';
import { ArrowUpRight, Building2, Grid2X2, Microscope, PackageCheck, Search, X } from 'lucide-react';
import { catalogueCategories, catalogueItems, catalogueEnquiryPath, filterCatalogueItems } from '@/lib/equipment-catalogue';
import { ProductPhoto } from '@/components/product-photo';

const categoryIcons = [Building2, Microscope, PackageCheck];

export function SolutionBrowser() {
  const [categoryId, setCategoryId] = useState('all');
  const [query, setQuery] = useState('');
  const searchId = useId();
  const headingId = useId();
  const categoriesId = useId();
  const results = filterCatalogueItems(categoryId, query);

  return (
    <section className="equipment-browser" id="equipment-catalogue" aria-labelledby={headingId}>
      <div className="catalogue-heading">
        <p className="eyebrow">FIND YOUR SOLUTION</p>
        <h2 id={headingId}>Browse equipment &amp; supplies.</h2>
        <p>Find the equipment type you need, then share your specifications for a quotation.</p>
      </div>
      <div className="catalogue-layout">
        <aside className="catalogue-sidebar" aria-labelledby={categoriesId}>
          <h3 id={categoriesId}>Categories</h3>
          <div className="catalogue-categories" role="group" aria-label="Filter equipment by category">
            <button type="button" className={categoryId === 'all' ? 'is-selected' : ''} aria-pressed={categoryId === 'all'} onClick={() => setCategoryId('all')}>
              <Grid2X2 size={19} aria-hidden="true" /><span>All equipment &amp; supplies</span><small>{catalogueItems.length}</small>
            </button>
            {catalogueCategories.map((category, index) => {
              const Icon = categoryIcons[index];
              return <button key={category.id} type="button" className={categoryId === category.id ? 'is-selected' : ''} aria-pressed={categoryId === category.id} onClick={() => setCategoryId(category.id)}>
                <Icon size={19} aria-hidden="true" /><span>{category.title}</span><small>{category.items.length}</small>
              </button>;
            })}
          </div>
          <div className="catalogue-support">
            <p>Need help with your specification?</p>
            <a href="/services/procurement-consultancy/" className="text-link">Equipment planning support</a>
          </div>
        </aside>
        <div className="catalogue-results">
          <form className="catalogue-search" role="search" aria-label="Equipment and supplies" onSubmit={event => event.preventDefault()}>
            <label htmlFor={searchId}>Search equipment &amp; supplies</label>
            <div className="catalogue-search-field">
              <Search size={20} aria-hidden="true" />
              <input id={searchId} type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Try PCR, patient monitoring or PPE" autoComplete="off" />
              {query && <button type="button" aria-label="Clear search" onClick={() => setQuery('')}><X size={19} aria-hidden="true" /></button>}
            </div>
          </form>
          <div className="catalogue-results-bar">
            <p role="status" aria-live="polite" aria-atomic="true">{results.length} equipment {results.length === 1 ? 'type' : 'types'}{categoryId !== 'all' && ' in ' + catalogueCategories.find(category => category.id === categoryId)?.title.toLowerCase()}</p>
            {(query || categoryId !== 'all') && <button type="button" className="small-link" onClick={() => { setQuery(''); setCategoryId('all'); }}>Reset filters</button>}
          </div>
          {results.length ? <div className="catalogue-grid">
            {results.map(item => {
              const Icon = categoryIcons[catalogueCategories.findIndex(category => category.id === item.categoryId)];
              return <article className="catalogue-card" key={item.id}>
                <ProductPhoto image={item.image} />
                <div className="catalogue-card-content">
                <div className="catalogue-card-category"><Icon size={19} aria-hidden="true" /><span>{item.categoryTitle}</span></div>
                <h3>{item.name}</h3>
                <a href={item.path} className="catalogue-detail-link">View solution <ArrowUpRight size={16} aria-hidden="true" /><span className="sr-only">: {item.name}</span></a>
                <a href={catalogueEnquiryPath(item)} className="catalogue-enquiry-link">Request a quotation <ArrowUpRight size={17} aria-hidden="true" /><span className="sr-only"> for {item.name}</span></a>
                </div>
              </article>;
            })}
          </div> : <div className="catalogue-empty"><Search size={29} aria-hidden="true" /><h3>No matching equipment types.</h3><p>Try a broader search or reset the filters. You can also send your requirements to our team.</p><button className="button secondary" type="button" onClick={() => { setQuery(''); setCategoryId('all'); }}>Show all equipment types</button><a href="/contact/" className="text-link">Discuss your requirements</a></div>}
          <p className="catalogue-note">Images illustrate equipment types. Models, availability, pricing and delivery are confirmed with your quotation.</p>
        </div>
      </div>
    </section>
  );
}
